import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { contextApi, ContextBalances, PairBalance } from '../../services/contextApi';
import { memberFirstName } from '../../services/balances';
import { localTodayIso } from '../../utils/bills';

interface Props {
  open: boolean;
  balances: ContextBalances;
  pair: PairBalance | null;
  currentUserId?: string;
  onClose: () => void;
  /** Called after the payment is recorded, so the caller reloads balances. */
  onSettled: () => void;
}

/**
 * "I paid this member back", or, from the receiver's side, "they paid me".
 * Amount defaults to the whole debt; a partial payment leaves the rest open,
 * and the balances reflect it right away.
 */
export const SettleDialog: React.FC<Props> = ({ open, balances, pair, currentUserId, onClose, onSettled }) => {
  const { t, i18n } = useTranslation('dashboard');
  const [amount, setAmount] = useState('');
  // The user's own calendar day: the UTC one is already tomorrow after 21h in Brazil.
  const [date, setDate] = useState(localTodayIso());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !pair) return;
    setAmount(String(pair.amount));
    setDate(localTodayIso());
    setNote('');
    setError(null);
  }, [open, pair]);

  if (!pair) {
    return null;
  }

  const receiver = memberFirstName(balances, pair.toUserId, currentUserId, t('balances.you'));
  const payer = memberFirstName(balances, pair.fromUserId, currentUserId, t('balances.you'));
  // The receiver records a payment made to them; anyone else records their own.
  const received = Boolean(currentUserId) && pair.toUserId === currentUserId;
  // More than the debt turns the rest into a debt the other way round: say
  // so before it is recorded, as the bot does.
  const typed = parseFloat(amount.replace(',', '.'));
  const excess =
    Number.isFinite(typed) && Math.round(typed * 100) > Math.round(pair.amount * 100)
      ? (Math.round(typed * 100) - Math.round(pair.amount * 100)) / 100
      : 0;
  const money = (value: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: balances.currency }).format(value);

  const submit = async () => {
    const value = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      setError(t('balances.invalidAmount'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await contextApi.settle(balances.contextId, {
        ...(received ? { fromUserId: pair.fromUserId } : {}),
        toUserId: pair.toUserId,
        amount: Math.round(value * 100) / 100,
        date,
        note: note.trim() || undefined,
      });
      onSettled();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || t('balances.failed'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{received ? t('balances.receivedTitle') : t('balances.settleTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {received ? t('balances.receivedHint', { from: payer }) : t('balances.settleHint', { to: receiver })}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('balances.amount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('balances.date')}
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          {excess > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {received
                  ? t('balances.overpayReceived', { debt: money(pair.amount), from: payer, excess: money(excess) })
                  : t('balances.overpay', { debt: money(pair.amount), to: receiver, excess: money(excess) })}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label={t('balances.note')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              inputProps={{ maxLength: 500 }}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('balances.cancel')}
        </Button>
        <Button onClick={submit} variant="contained" disabled={isSaving}>
          {t('balances.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
